import type { RadarBubble, RadarBubbleLesson } from '@/services/api/teacher'
import { GlassTooltipContent } from './GlassTooltipContent'
import { detectLocale, getTranslations } from '../i18n'
import { formatDateShort } from '../i18n/formatters'

/**
 * Shared RadarBubble -> GlassTooltipContent renderer.
 *
 * Single Source of Truth for how a RadarBubble maps onto the glass tooltip/panel
 * layout. Consumed by BOTH the cursor-following FloatingTooltip AND the
 * HoveredBubbleInfo panel under the radar, so the two surfaces cannot drift
 * (same fields, same positions, same optional badges: prep / cascade summary /
 * requester+multi-request footer).
 */
export function RadarBubbleContent({ bubble, isAdminView = false }: { bubble: RadarBubble; isAdminView?: boolean }) {
  const isIncoming = bubble.bubble_type === 'incoming'

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleDateString('de-CH')
  }

  // Header: Admin-Radar zeigt den anfragenden Lehrercode (z.B. "Anfragen von WOM"),
  // Teacher-Radar behaelt "Anfrage von mir" / "Anfrage an mich".
  const headerLabel = isIncoming
    ? 'Anfrage an mich'
    : isAdminView
      ? `Anfragen von ${bubble.requester_short_code || '???'}`
      : 'Anfrage von mir'

  // Single-lesson bubbles may carry only bubble-level subject/klass/room without
  // a populated `lessons[]`. Synthesize a fallback so the single-line renders.
  const rawLessons: RadarBubbleLesson[] = bubble.lessons ?? []
  const lessons = rawLessons.length > 0
    ? rawLessons
    : [
        {
          date: bubble.lesson_date,
          time_slot_name: bubble.time_slot_name,
          subject_name: bubble.subject_name,
          klass_name: bubble.klass_name,
          room_name: bubble.room_name,
          status: bubble.status,
          assigned_teacher_short_code: bubble.assigned_teacher_short_code,
          assigned_teacher_name: bubble.assigned_teacher_name,
        },
      ]
  const hasMultiple = lessons.length > 1

  // assigned-fix-v1: zugewiesene Lehrer aus den Lektionen ableiten (dedupliziert).
  // Dient als Fallback für den "Übernommen von"-Hover, wenn keine Kaskaden-Daten
  // existieren (direkte Anfragen), und für das Status-Label.
  const assignedTeachers = (() => {
    const byCode = new Map<string, { code: string; name?: string | null }>()
    for (const l of lessons) {
      const code = l.assigned_teacher_short_code
      if (code && !byCode.has(code)) {
        byCode.set(code, { code, name: l.assigned_teacher_name })
      }
    }
    return Array.from(byCode.values())
  })()

  const countLabel = hasMultiple
    ? ` · ${lessons.length} Lektionen`
    : bubble.time_slot_name
      ? ` ${bubble.time_slot_name}`
      : undefined

  const partialLabel =
    !bubble.is_split &&
    bubble.effective_lesson_count !== undefined &&
    bubble.original_lesson_count !== undefined &&
    bubble.effective_lesson_count !== bubble.original_lesson_count
      ? `${bubble.effective_lesson_count}/${bubble.original_lesson_count} offen`
      : undefined

  const showTeacherSummary = !isIncoming && (bubble.teachers_contacted ?? 0) > 0
  const teacherSummary = showTeacherSummary
    ? {
        contacted: bubble.teachers_contacted ?? 0,
        accepted: bubble.teachers_accepted ?? 0,
        declined: bubble.teachers_declined ?? 0,
        pending: bubble.teachers_pending ?? 0,
      }
    : undefined

  // Footer note: show BOTH the requester (incoming) and the multi-request count
  // when applicable — previously a ternary silently dropped one of them.
  const noteParts: string[] = []
  if (isIncoming && bubble.requester_name) noteParts.push(`Von: ${bubble.requester_name}`)
  if (bubble.request_ids && bubble.request_ids.length > 1) {
    noteParts.push(`${bubble.request_ids.length} Anfragen`)
  }
  const footerNote = noteParts.length > 0 ? noteParts.join(' · ') : undefined

  // Dynamic status label: human-readable text for the radar tooltip.
  const STATUS_DISPLAY: Record<string, string> = {
    interested: 'Wartepool',
    pending: 'Offen',
    declined: 'Abgelehnt',
    filled: 'Übernommen',
    accepted: 'Übernommen',
    assigned: 'Übernommen',
    booked: 'Übernommen',
    cancelled: 'Storniert',
    expired: 'Abgelaufen',
  }
  const statusLabel = bubble.status === 'assigned_elsewhere' && bubble.assigned_teacher_short_code
    ? `Übernommen von ${bubble.assigned_teacher_short_code}`
    : (STATUS_DISPLAY[bubble.status] ?? bubble.status)

  // assigned-fix-v1: auch bei filled/accepted/assigned/booked anzeigen, WER
  // übernommen hat — aber nur, wenn alle zugewiesenen Lektionen dieselbe
  // Lehrperson betreffen (bei mehreren zuständigen Lehrern zeigt die
  // "Übernommen von"-Badge-Liste die Details).
  const isFilledStatus = ['filled', 'accepted', 'assigned', 'booked'].includes(bubble.status)
  const singleAssigned = isFilledStatus && assignedTeachers.length === 1 ? assignedTeachers[0] : undefined
  const finalStatusLabel = singleAssigned
    ? `Übernommen von ${singleAssigned.code}`
    : statusLabel

  // frost-v2 (E1/E11/E13): Gate-Frost — Chip ersetzt Statuschip, Zeile mit
  // «seit {Datum}» nur wenn gate_since vorhanden (R2-Fallback ohne).
  const locale = detectLocale()
  const gatePending = !!bubble.gate_pending && bubble.bubble_type !== 'incoming'
  const gateSinceLabel = gatePending && bubble.gate_since
    ? formatDateShort(bubble.gate_since, locale)
    : null
  const gateI18n = getTranslations(locale).tooltip
  const gateChipLabel = gatePending ? gateI18n.gateChip : undefined
  const gateLineLabel = gatePending
    ? gateSinceLabel
      ? gateI18n.gateLine(gateSinceLabel)
      : gateI18n.gateLineNoDate
    : undefined

  return (
    <GlassTooltipContent
      dotColor={bubble.bubble_color}
      dotVariant={isIncoming ? 'ring' : 'solid'}
      headerLabel={headerLabel}
      dateLabel={formatDate(bubble.lesson_date)}
      countLabel={countLabel}
      lessons={lessons}
      statusLabel={gatePending ? undefined : finalStatusLabel}
      hoursLabel={`${bubble.hours_until_lesson.toFixed(1)}h`}
      partialLabel={partialLabel}
      prepLabel={bubble.prep_requested ? 'Prep angefordert' : bubble.prep_open ? 'Prep offen' : undefined}
      prepUrgent={!!bubble.prep_requested}
      directorPending={!!bubble.director_action_pending}
      unreadChat={!!bubble.has_unread}
      gatePending={gatePending}
      gateChipLabel={gateChipLabel}
      gateLineLabel={gateLineLabel}
      stornoPending={bubble.cancellation_pending}
      teacherSummary={teacherSummary}
      splitRole={bubble.split_role}
      cascadeTeachers={bubble.cascade_teachers}
      assignedTeachers={assignedTeachers}
      footerNote={footerNote}
    />
  )
}
